import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Phone } from "lucide-react";
import temp from "../assets/hs1.jpg";

export default function ContactUs() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-120px" });

  /* =========================
     STATE MANAGEMENT
  ========================== */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  /* =========================
     HANDLE INPUT CHANGE
  ========================== */
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  /* =========================
     HANDLE SUBMIT
  ========================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const res = await fetch  (
        `${process.env.REACT_APP_BACKEND_URL}/contact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const data = await res.json();

      if (data.success) {
        alert("Message sent successfully!");

        setFormData({
          name: "",
          email: "",
          phone: "",
          address: "",
          service: "",
          message: "",
        });
      } else {
        alert(data.message || "Failed to send message");
      }
    } catch (error) {
      console.error("MAIL ERROR:", error);
      alert("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     ANIMATIONS
  ========================== */
  const formContainer = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.12 },
    },
  };

  const inputItem = {
    hidden: { opacity: 0, y: 35 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <div ref={ref} className="w-full bg-white">
      {/* HERO SECTION */}
      <div className="relative h-[300px] bg-[#0928A6] overflow-hidden">
        <motion.img
          src={temp}
          alt="Banner"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6 }}
        />

        <div className="relative z-10 flex flex-col items-center justify-center h-full text-white text-center">
          <h1 className="text-4xl font-bold">Contact Us</h1>
        </div>

        <div className="absolute bottom-0 w-full">
          <svg viewBox="0 0 1440 120" className="w-full h-[90px]">
            <path
              fill="#fff"
              d="M0,60 C360,120 1080,0 1440,60 L1440,120 L0,120 Z"
            />
          </svg>
        </div>
      </div>

      {/* MAIN SECTION */}
      <div className="max-w-[1150px] mx-auto px-6 py-20 grid lg:grid-cols-[380px_1fr] gap-14">
        {/* LEFT INFO */}
        <div className="space-y-6">
          <div className="bg-white shadow-md border rounded-lg p-6 flex gap-4">
            <div className="w-12 h-12 bg-blue-600 text-white flex items-center justify-center rounded">
              🏢
            </div>
            <div>
              <h3 className="font-bold text-gray-800">Visit Office</h3>
              <p className="text-sm text-gray-600 mt-1">
                H No. 3-1/145, 5th floor, Neknampur, Gandipet, Manikonda,K.V. Rangareddy dist. 500089
              </p>
            </div>
          </div>

          <div className="bg-[#0928A6] text-white shadow-lg rounded-lg p-6 flex gap-4">
            <div className="w-12 h-12 bg-white text-[#0928A6] flex items-center justify-center rounded">
              ✉️
            </div>
            <div>
              <h3 className="font-bold">Send Email</h3>
              <p className="text-sm opacity-90 mt-1">
                info@goodwillglobalsolutions.com
              </p>
            </div>
          </div>

          <div className="bg-white shadow-md border rounded-lg p-6 flex gap-4">
            <div className="w-12 h-12 bg-blue-600 text-white flex items-center justify-center rounded">
              <Phone className="text-white w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800">Call Us</h3>
              <p className="text-sm text-gray-600 mt-1">
                +91 7386252108
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT FORM */}
        <motion.div
          variants={formContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="bg-white shadow-xl rounded-lg p-10 border"
        >
          <motion.h2 variants={inputItem} className="text-xl font-bold mb-6">
            Drop Your Message
          </motion.h2>

          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-5">
            <motion.input
              variants={inputItem}
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Name"
              className="border p-3 rounded w-full"
            />

            <motion.input
              variants={inputItem}
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              className="border p-3 rounded w-full"
            />

            <motion.input
              variants={inputItem}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone"
              className="border p-3 rounded w-full"
            />

            <motion.input
              variants={inputItem}
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Address"
              className="border p-3 rounded w-full"
            />

            <motion.select
              variants={inputItem}
              name="service"
              value={formData.service}
              onChange={handleChange}
              className="border p-3 rounded col-span-2"
            >
              <option value="">Select Service</option>
              <option value="Consulting">Consulting</option>
              <option value="Visa Guidance">Visa Guidance</option>
              <option value="Education">Education</option>
            </motion.select>

            <motion.textarea
              variants={inputItem}
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              placeholder="Message"
              className="border p-3 rounded col-span-2"
            />

            <motion.button
              type="submit"
              disabled={loading}
              className="bg-[#0928A6] text-white py-3 rounded col-span-2 font-semibold"
            >
              {loading ? "Sending..." : "Send A Message"}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}